import { MRT_Row } from "material-react-table";
import type { Job, Deal } from "../../../../../../models";
import { isFieldRequired } from "../../../../../../utilities/jobValidation";
import { ColumnContext } from "../standard-column-types";
import { DealColumnViewModel, DealOption } from "./Deal.column.types";
import { updateJobFromDeal, updateRowCacheFromJob } from "./Deal.column.helpers";

/**
 * Hook that provides the DealColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useDealColumnViewModel(context: ColumnContext): DealColumnViewModel {
	/**
	 * Get current deal value for row
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getCurrentDeal(job: Job): DealOption {
		return (
			context.props.allDeals.find((deal) => deal.id === job?.dealIds?.[0]) || {
				id: "custom",
			}
		);
	}

	/**
	 * Compute required state from context
	 */
	function getIsRequired(row: MRT_Row<Job>): boolean {
		const rowContext = context.getRowValidationContext(row);
		return isFieldRequired("deal", rowContext);
	}

	/**
	 * Handle deal change for Cell (updates multiple job fields)
	 * Desktop passes row for MRT cache updates; mobile omits it
	 */
	function handleDealChange(jobId: string, newDeal: DealOption | null, row?: MRT_Row<Job>): void {
		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;

		const index = newJobs.indexOf(newJob);
		newJob.dealIds = [newDeal?.id || "custom"];

		if (newJob.dealIds.at(0) !== "custom") {
			updateJobFromDeal(newJob, newDeal as Deal);
		}

		// Update row cache for MRT compatibility (desktop only)
		if (row) {
			updateRowCacheFromJob(row, newJob);
		}
		newJobs[index] = newJob;
		context.setJobs(newJobs);
		context.validateJobs([newJob], { updateState: true, clearPrevious: false });
	}

	/**
	 * Handle group deal change for AggregatedCell
	 */
	function handleGroupDealChange(row: MRT_Row<Job>, newDeal: DealOption | null): void {
		const dealId = newDeal?.id || "custom";
		const d = newDeal as Deal;

		const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
			job.dealIds = [dealId];
			if (dealId !== "custom" && d) {
				job.importDensity = d.density;
				job.importDepth = d.depth;
				job.externalTestPackageId = d.testPackage || "No test package specified";
				job.externalFrequencyOfMicros = d.frequencyOfMicros ?? 0;
			}
		});

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getCurrentDeal,
		getIsRequired,
		handleDealChange,
		handleGroupDealChange,
	};
}
